// 웹이 쓰는 RPC 클라이언트 — 타입은 ACTIONS 에서 꺼내고(런타임 import 없음) fetch 하나로 봉투를 푼다.

import type { AuthSessionChange } from "@buildpy/core";
import type { z } from "zod";

import type { Invalidation } from "./http/envelope";
import type { SignedUrl } from "./http/signed-url";
import type { ACTIONS, ActionKey } from "./rpc/registry";
import type { VIEWS, ViewKey } from "./views/registry";
import type { ViewQuery } from "./views/types";

type Actions = typeof ACTIONS;
type FnOf<K extends ActionKey> = Actions[K]["fn"];

type Flat<T> = { [P in keyof T]: T[P] };
type UndefinedKeys<T> = { [P in keyof T]-?: undefined extends T[P] ? P : never }[keyof T];
// 배열 원소 undefined 는 JSON.stringify 가 null 로 쓴다.
type JsonElement<T> = undefined extends T ? Jsonified<Exclude<T, undefined>> | null : Jsonified<T>;
// 속성 undefined 는 JSON.stringify 가 뺀다 — undefined 가 낄 수 있는 속성은 선택 속성이 된다.
type JsonObject<T> = Flat<
  { [P in Exclude<keyof T, UndefinedKeys<T>>]: Jsonified<T[P]> } & { [P in UndefinedKeys<T>]?: Jsonified<Exclude<T[P], undefined>> }
>;

// JSON 을 한 번 거친 모양(Response.json) — Date 는 ISO 문자열 · 속성 undefined 는 빠지고 배열 원소 undefined 는 null · 최상위 undefined 는 RpcResult 가 null 로 바꾼다.
export type Jsonified<T> = T extends Date
  ? string
  : T extends readonly (infer U)[]
    ? JsonElement<U>[]
    : T extends object
      ? JsonObject<T>
      : T;

export type RpcArgs<K extends ActionKey> = Parameters<FnOf<K>>;
// 서버가 undefined 를 null 로 보낸다 — void·undefined(유니온 속 undefined 포함)를 null 로 바꾼다.
type UndefinedToNull<T> = undefined extends T ? Exclude<T, undefined | void> | null : T;
export type RpcResult<K extends ActionKey> = Jsonified<UndefinedToNull<Awaited<ReturnType<FnOf<K>>>>>;

type KeysOf<A> = { [K in ActionKey]: Actions[K]["access"] extends A ? K : never }[ActionKey];
// user = 세션만 있고 조직이 아직 없는 계정(셀프 입점) — 같은 Bearer 로 보낸다.
export type MemberOrPublicKey = KeysOf<"member" | "public" | "user">;
export type ShareKind = "s" | "c";
export type ShareKey<S extends ShareKind> = KeysOf<`share:${S}`>;
type ExportOf<K extends string> = K extends `${string}/${infer E}` ? (E extends `${string}/${string}` ? ExportOf<E> : E) : never;
export type ShareExport<S extends ShareKind> = ExportOf<ShareKey<S>>;
type ShareKeyOf<S extends ShareKind, E extends ShareExport<S>> = Extract<ShareKey<S>, `${string}/${E}`>;

type Views = typeof VIEWS;
type ViewKeysOf<A> = { [K in ViewKey]: Views[K]["access"] extends A ? K : never }[ViewKey];
export type { SignedUrl, ViewKey, ViewQuery };
export type MemberViewKey = ViewKeysOf<"member">;
export type ShareViewKey<S extends ShareKind> = ViewKeysOf<`share:${S}`>;
export type PublicViewKey = ViewKeysOf<"public">;
export type ViewParams<K extends ViewKey> = z.input<Views[K]["params"]>;
export type ViewResult<K extends ViewKey> = Jsonified<UndefinedToNull<Awaited<ReturnType<Views[K]["load"]>>>>;
// 고객 링크의 token · 공개의 path 는 주소에서 handler 가 넣는다 — 쿼리로 보내지 않는다.
// Omit 은 제네릭 K 에서 ViewQuery 로 좁혀지지 않는다 — 레지스트리의 satisfies ViewEntry 가 값 모양을 보장해 호출부에서 단언한다.
export type QueryOf<K extends ViewKey, Fixed extends string> = Omit<ViewParams<K>, Fixed>;
// 빈 객체로 충분하면 인자를 생략할 수 있다.
export type OptionalArg<P> = {} extends P ? [params?: P] : [params: P];

// 서명 URL 한 번에 받는 상한 — 화면 하나의 사진을 한 요청에(분당 한도를 아낀다) · 라우트와 웹이 같은 값을 읽는다.
export const SIGNED_URL_BATCH_MAX = 200;

export const ARGS_FIELD = "$args";
export const FORM_MARKER = "$form";
export const UNDEF_MARKER = "$undef";

// 최상위 인자 undefined 의 자리 표식 — JSON 은 배열 원소 undefined 를 null 로 바꿔 선택 인자(zod optional)가 깨진다(legacy 서버 액션은 undefined 를 그대로 보냈다).
export const UNDEF_VALUE = { [UNDEF_MARKER]: true } as const;

const hasMarkerKey = (arg: unknown): boolean =>
  typeof arg === "object" && arg !== null && !Array.isArray(arg) && (Object.prototype.hasOwnProperty.call(arg, FORM_MARKER) || Object.prototype.hasOwnProperty.call(arg, UNDEF_MARKER));

export class RpcError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    // 이 요청에 실어 보낸 Bearer — 401 뒤 그 세션만 지우게(그 사이 로그인한 새 세션은 남긴다).
    readonly token: string | null = null
  ) {
    super(typeof (body as { message?: unknown })?.message === "string" ? (body as { message: string }).message : `rpc ${status}`);
    this.name = "RpcError";
  }
}

// 서버가 이동을 알렸다 — onRedirect 를 부른 뒤 던져서 액션 뒤 코드가 돌지 않게 한다(legacy redirect() 와 같다).
export class RpcRedirect extends Error {
  constructor(readonly to: string) {
    super(`redirect ${to}`);
    this.name = "RpcRedirect";
  }
}

export interface RpcClientOptions {
  baseUrl?: string;
  fetch?: (input: string, init?: RequestInit) => Promise<Response>;
  // 멤버 액션에 실을 Bearer — 없으면 헤더를 붙이지 않는다.
  getAccessToken?: () => string | null | Promise<string | null>;
  onRedirect?: (to: string) => void;
  onInvalidate?: (paths: Invalidation[]) => void;
  onSession?: (change: AuthSessionChange) => void;
}

interface Envelope {
  value?: unknown;
  redirect?: string;
  invalidate?: Invalidation[];
  session?: AuthSessionChange;
}

// FormData 가 하나라도 있으면 multipart — 그 자리는 표지로 바꾸고 필드는 본문에 그대로 싣는다 · undefined 자리는 두 경로 모두 표식.
export const encodeArgs = (args: readonly unknown[]): { body: string | FormData; json: boolean } => {
  const isForm = (arg: unknown): arg is FormData => typeof FormData !== "undefined" && arg instanceof FormData;
  if (args.some((arg) => !isForm(arg) && hasMarkerKey(arg))) throw new Error(`인자 객체에 ${FORM_MARKER}·${UNDEF_MARKER} 키를 쓸 수 없습니다`);
  const forms = args.filter(isForm);
  if (new Set(forms).size > 1) throw new Error("FormData 는 호출 하나에 하나만 보낼 수 있습니다");
  const marked = args.map((arg) => (arg === undefined ? UNDEF_VALUE : forms.length > 0 && arg === forms[0] ? { [FORM_MARKER]: true } : arg));
  if (forms.length === 0) return { body: JSON.stringify({ args: marked }), json: true };
  const body = new FormData();
  for (const [name, value] of forms[0].entries()) body.append(name, value);
  body.set(ARGS_FIELD, JSON.stringify(marked));
  return { body, json: false };
};

const isQueryValue = (item: unknown): boolean => typeof item === "string" || typeof item === "number" || typeof item === "boolean";

// 읽기 쿼리 — undefined·null 은 뺀다 · 배열은 같은 키를 되풀이한다(서버 queryOf 와 짝) · 객체·Date 는 던진다.
export const encodeViewQuery = (params: ViewQuery | undefined): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null) continue;
    for (const item of Array.isArray(value) ? (value as readonly unknown[]) : [value]) {
      if (item === undefined || item === null) continue;
      if (!isQueryValue(item)) throw new TypeError(`읽기 쿼리 ${key} 에 문자열·숫자·참거짓이 아닌 값을 실을 수 없습니다`);
      search.append(key, String(item));
    }
  }
  const text = search.toString();
  return text === "" ? "" : `?${text}`;
};

// required = 멤버 Bearer · optional = 있으면 싣고 세션을 못 읽으면(supabase env 없음 등) 빼고 보낸다 · none = 싣지 않는다.
type BearerMode = "required" | "optional" | "none";

export const createRpcClient = (options: RpcClientOptions = {}) => {
  const base = options.baseUrl ?? "";
  const doFetch = options.fetch ?? ((input: string, init?: RequestInit) => fetch(input, init));

  const tokenOf = async (bearer: BearerMode): Promise<string | null> => {
    if (bearer === "none" || !options.getAccessToken) return null;
    if (bearer === "required") return options.getAccessToken();
    try {
      return await options.getAccessToken();
    } catch {
      return null;
    }
  };

  const headersOf = async (bearer: BearerMode): Promise<{ headers: Record<string, string>; token: string | null }> => {
    const headers: Record<string, string> = { accept: "application/json" };
    const token = await tokenOf(bearer);
    if (token) headers.authorization = `Bearer ${token}`;
    return { headers, token: token ?? null };
  };

  // action = 액션 봉투(invalidate·onRedirect 를 따른다) · 읽기는 이동을 던지기만 한다(라우터 loader 가 처리).
  const open = async (res: Response, action: boolean, token: string | null): Promise<unknown> => {
    const parsed: unknown = await res.json().catch(() => null);
    if (!res.ok) throw new RpcError(res.status, parsed, token);
    const envelope = (parsed ?? {}) as Envelope;
    if (envelope.session) options.onSession?.(envelope.session);
    if (action && envelope.invalidate && envelope.invalidate.length > 0) options.onInvalidate?.(envelope.invalidate);
    if (typeof envelope.redirect === "string") {
      if (action) options.onRedirect?.(envelope.redirect);
      throw new RpcRedirect(envelope.redirect);
    }
    return envelope.value;
  };

  const post = async (path: string, args: readonly unknown[], bearer: BearerMode): Promise<unknown> => {
    const { body, json } = encodeArgs(args);
    const { headers, token } = await headersOf(bearer);
    if (json) headers["content-type"] = "application/json";
    return open(await doFetch(`${base}${path}`, { method: "POST", headers, body, credentials: "same-origin" }), true, token);
  };

  const get = async (path: string, bearer: BearerMode): Promise<unknown> => {
    const { headers, token } = await headersOf(bearer);
    return open(await doFetch(`${base}${path}`, { method: "GET", headers, credentials: "same-origin" }), false, token);
  };

  const rpc = <K extends MemberOrPublicKey>(key: K, ...args: RpcArgs<K>): Promise<RpcResult<K>> =>
    post(`/api/rpc/${key}`, args, "required") as Promise<RpcResult<K>>;

  // 고객 링크 액션 — 링크 쿠키가 /api/s|c/<token> 에 있어 그 주소로 보낸다(Bearer 없음).
  const shareRpc = <S extends ShareKind, E extends ShareExport<S>>(
    kind: S,
    token: string,
    name: E,
    ...args: RpcArgs<ShareKeyOf<S, E>>
  ): Promise<RpcResult<ShareKeyOf<S, E>>> =>
    post(`/api/${kind}/${encodeURIComponent(token)}/rpc/${name}`, args, "none") as Promise<RpcResult<ShareKeyOf<S, E>>>;

  const view = <K extends MemberViewKey>(name: K, ...params: OptionalArg<ViewParams<K>>): Promise<ViewResult<K>> =>
    get(`/api/views/${encodeURIComponent(name)}${encodeViewQuery(params[0])}`, "required") as Promise<ViewResult<K>>;

  // 고객 링크 읽기 — 링크 쿠키가 실리는 /api/s|c/<token> 아래로 보낸다(Bearer 없음).
  const shareView = <S extends ShareKind, K extends ShareViewKey<S>>(
    kind: S,
    token: string,
    name: K,
    ...params: OptionalArg<QueryOf<K, "token">>
  ): Promise<ViewResult<K>> =>
    get(`/api/${kind}/${encodeURIComponent(token)}/views/${encodeURIComponent(name)}${encodeViewQuery(params[0] as ViewQuery | undefined)}`, "none") as Promise<ViewResult<K>>;

  // 공개 읽기 — path 조각은 하나씩 인코딩한다 · Bearer 는 선택(세션을 못 읽으면 없이 보낸다).
  const publicView = <K extends PublicViewKey>(name: K, path: readonly string[] = [], ...params: OptionalArg<QueryOf<K, "path">>): Promise<ViewResult<K>> => {
    const rest = path.map((part) => `/${encodeURIComponent(part)}`).join("");
    return get(`/api/p/${encodeURIComponent(name)}${rest}${encodeViewQuery(params[0] as ViewQuery | undefined)}`, "optional") as Promise<ViewResult<K>>;
  };

  // 서명 URL 묶음 — img·a 는 Bearer 를 못 실어 /api/signed-url 이 같은 순서로 `?org=&exp=&sig=` 주소를 준다.
  const signUrls = async (paths: readonly string[]): Promise<SignedUrl[]> => {
    const { headers, token } = await headersOf("required");
    const res = await doFetch(`${base}/api/signed-url`, { method: "POST", headers: { ...headers, "content-type": "application/json" }, body: JSON.stringify({ paths }), credentials: "same-origin" });
    return (await open(res, false, token)) as SignedUrl[];
  };

  return { rpc, shareRpc, view, shareView, publicView, signUrls };
};
